/**
 * AtlasDroidCommandModule — deployed droids on the Commons (the SWG
 * datapad-pet lineage): deploy a crafted droid item into the world, walk up
 * to it (K) and work its TERMINAL — immediate orders (stop/run/scan/come/
 * follow/home/recall) plus a Droidscript program editor and the droid's own
 * log. Every deployed droid (yours and anyone's) renders at its
 * authoritative position; a 1 Hz system drives `tickDroids` for the local
 * owner's units so programs run wherever the owner wanders.
 *
 * The droid is STRICTLY an observer: it moves, scans (mineral concentration
 * fields, wildlife), and logs — the server never lets it touch the world.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { buildDroidMesh } from './AtlasDroidModule.js';
import { groundYIn } from '../lib/grounding.js';
import { sfx } from '../lib/sfx.js';
import { markShared, deferDisposeTree } from '../lib/defer_dispose.js';
import { prefabAssetId } from '../lib/player_models.js';
import type { AssetModule } from './AssetModule.js';

const TERMINAL_RANGE_M = 10;
const TICK_EVERY_S = 1;

const CHEAT_SHEET =
  'goto <x> <z> · move n|s|e|w <m> · patrol <r> · scan [life] · wait <s> · home · say <text> · loop   (; comments)';

const SAMPLE = `; survey loop — edit me
patrol 40
scan
wait 20
scan life
home
loop`;

interface DroidRow {
  droidId: bigint;
  owner: { toHexString(): string; isEqual(o: unknown): boolean };
  worldId: bigint;
  kind: string;
  name: string;
  mode: string;
  posX: number;
  posZ: number;
  yaw: number;
  programSrc: string;
  logJson: string;
}

export class AtlasDroidCommandModule implements IModule {
  readonly name = 'AtlasDroidCommand';

  private app: App | null = null;
  private assets: AssetModule | null = null;
  private units = new Map<string, { obj: THREE.Object3D; kind: string; bob: number; fallback: boolean }>();
  private masters = new Map<string, THREE.Object3D>();
  private loading = new Set<string>();

  constructor(assets: AssetModule | null = null) {
    this.assets = assets;
  }

  /** The fleet wave's droid GLB (prop:droid_*) — normalized to unit scale;
   *  null while loading/absent (the primitive orb covers the gap). */
  private droidMaster(kind: string): THREE.Object3D | null {
    const have = this.masters.get(kind);
    if (have) return have;
    const conn = atlas.conn;
    if (!conn || !this.assets || this.loading.has(kind)) return null;
    const id = prefabAssetId(conn, `prop:${kind}`);
    if (id === null) return null;
    this.loading.add(kind);
    this.assets.load(id)
      .then((cached) => {
        // Never mutate the shared AssetModule cache (SpaceModule docks the same
        // droid GLB at the ship's droid port) — normalize a private clone.
        const obj = cached.clone();
        const box = new THREE.Box3().setFromObject(obj);
        const size = new THREE.Vector3();
        box.getSize(size);
        const longest = Math.max(size.x, size.y, size.z);
        if (longest > 1e-3) obj.scale.multiplyScalar(1.1 / longest);
        markShared(obj); // clones tear down freely; the master's buffers survive
        this.masters.set(kind, obj);
      })
      .catch(() => { /* primitive covers it */ })
      .finally(() => { this.loading.delete(kind); });
    return null;
  }

  private buildUnit(kind: string): { obj: THREE.Object3D; fallback: boolean } {
    const master = this.droidMaster(kind);
    if (master) {
      const wrap = new THREE.Group();
      wrap.add(master.clone());
      return { obj: wrap, fallback: false };
    }
    return { obj: buildDroidMesh(kind), fallback: true };
  }
  private tickAcc = 0;
  private win: HTMLDivElement | null = null;
  private winDroid: bigint | null = null;
  private hint: HTMLDivElement | null = null;
  private logBox: HTMLDivElement | null = null;
  private statusLine: HTMLDivElement | null = null;
  private srcBox: HTMLTextAreaElement | null = null;
  private logTimer: number | null = null;

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.repeat || e.code !== 'KeyK' || e.ctrlKey || e.metaKey || e.altKey) return;
    const tag = (e.target as HTMLElement | null)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;
    if (atlas.spaceFlight || atlas.flyMode || atlas.loadingGate) return;
    if (this.win) { this.closeWin(); return; }
    const near = this.nearestOwnDroid();
    if (near) this.openWin(near);
  };

  init(app: App): void {
    this.app = app;
    app.world.addSystem(makeSystem('DroidUnits', Phase.Animation, 40, (dt) => this.frame(dt)));
    window.addEventListener('keydown', this.onKeyDown);
    bus.on('atlas:world-cleared', () => { this.closeWin(); this.clearUnits(); });
    const hint = document.createElement('div');
    hint.style.cssText = 'position:fixed;bottom:296px;left:50%;transform:translateX(-50%);z-index:40;'
      + 'font:600 13px system-ui;color:#9fd0ff;background:rgba(8,14,26,.72);border:1px solid #2a3b55;'
      + 'border-radius:8px;padding:5px 12px;display:none;pointer-events:none';
    document.body.appendChild(hint);
    this.hint = hint;
    // Probe/dev hook.
    (window as unknown as Record<string, unknown>).__droid = {
      deploy: (itemId: string) => atlas.conn?.reducers.deployDroid({ itemId: BigInt(itemId) }),
      open: () => { const n = this.nearestOwnDroid(); if (n) this.openWin(n); return n?.droidId?.toString() ?? null; },
      units: () => [...this.units.keys()],
    };
  }

  // ─── data ────────────────────────────────────────────────────────────────

  private rows(): DroidRow[] {
    const conn = atlas.conn;
    const out: DroidRow[] = [];
    if (!conn || atlas.currentWorldId === null) return out;
    try {
      for (const d of conn.db.droidUnit.iter()) {
        if (d.worldId === atlas.currentWorldId) out.push(d as unknown as DroidRow);
      }
    } catch { /* row cache mid-churn */ }
    return out;
  }

  private nearestOwnDroid(rows: DroidRow[] = this.rows()): DroidRow | null {
    const conn = atlas.conn;
    if (!conn?.identity) return null;
    let best: DroidRow | null = null;
    let bestD = TERMINAL_RANGE_M;
    for (const d of rows) {
      if (!d.owner.isEqual(conn.identity)) continue;
      const dist = Math.hypot(d.posX - atlas.localX, d.posZ - atlas.localZ);
      if (dist < bestD) { bestD = dist; best = d; }
    }
    return best;
  }

  // ─── render + tick ───────────────────────────────────────────────────────

  private frame(dt: number): void {
    const conn = atlas.conn;
    const scene = this.app?.world.scene;
    if (!conn || !scene) return;
    // Drive my own droids' programs (wall-clock dt lives server-side).
    this.tickAcc += dt;
    if (this.tickAcc >= TICK_EVERY_S) {
      this.tickAcc = 0;
      let mine = 0;
      for (const d of this.rows()) if (conn.identity && d.owner.isEqual(conn.identity) && d.mode !== 'idle') mine++;
      if (mine > 0) conn.reducers.tickDroids({}).catch(() => { /* transient */ });
    }
    // Render every deployed unit in this world (ONE row pass per frame — the
    // terminal hint below reuses it instead of a second droid_unit scan).
    const rows = this.rows();
    const seen = new Set<string>();
    for (const d of rows) {
      const key = d.droidId.toString();
      seen.add(key);
      let u = this.units.get(key);
      // The GLB master may resolve after the primitive spawned — upgrade in place.
      if (u && u.fallback && this.masters.has(d.kind)) {
        scene.remove(u.obj);
        deferDisposeTree(u.obj);
        u = undefined;
      }
      if (!u) {
        const built = this.buildUnit(d.kind);
        u = { obj: built.obj, kind: d.kind, bob: Math.random() * 6, fallback: built.fallback };
        scene.add(u.obj);
        u.obj.position.set(d.posX, 0, d.posZ);
        this.units.set(key, u);
      }
      u.bob += dt;
      const k = 1 - Math.pow(0.002, dt);
      u.obj.position.x += (d.posX - u.obj.position.x) * k;
      u.obj.position.z += (d.posZ - u.obj.position.z) * k;
      const ground = groundYIn(conn, atlas.currentWorldId, u.obj.position.x, u.obj.position.z);
      u.obj.position.y = ground + 1.15 + Math.sin(u.bob * 2.1) * 0.07;
      u.obj.rotation.y = d.yaw;
    }
    for (const [key, u] of this.units) {
      if (!seen.has(key)) {
        scene.remove(u.obj);
        deferDisposeTree(u.obj);
        this.units.delete(key);
      }
    }
    // Terminal hint — DOM written only when the text changes (was every frame).
    if (this.hint) {
      const near = !this.win && !atlas.spaceFlight ? this.nearestOwnDroid(rows) : null;
      const text = near ? `K — droid terminal (${near.name})` : '';
      if (text !== this.hintText) {
        this.hintText = text;
        this.hint.textContent = text;
        this.hint.style.display = near ? 'block' : 'none';
      }
    }
  }
  private hintText = '';

  private clearUnits(): void {
    const scene = this.app?.world.scene;
    for (const [, u] of this.units) { scene?.remove(u.obj); deferDisposeTree(u.obj); }
    this.units.clear();
  }

  // ─── terminal window ─────────────────────────────────────────────────────

  private openWin(d: DroidRow): void {
    this.closeWin();
    this.winDroid = d.droidId;
    const win = document.createElement('div');
    win.style.cssText = 'position:fixed;top:70px;right:16px;width:340px;z-index:70;'
      + 'background:rgba(10,14,22,.94);color:#dde;border:1px solid #2a3b55;border-radius:10px;'
      + 'font:12px/1.5 system-ui;padding:12px 14px;box-shadow:0 6px 24px rgba(0,0,0,.5)';
    const title = document.createElement('div');
    title.textContent = `⚙ ${d.name} — droid terminal`;
    title.style.cssText = 'font-weight:700;color:#9cf;margin-bottom:2px';
    const status = document.createElement('div');
    status.style.cssText = 'color:#89a;margin-bottom:8px';
    this.statusLine = status;

    const btnRow = document.createElement('div');
    btnRow.style.cssText = 'display:flex;flex-wrap:wrap;gap:5px;margin-bottom:8px';
    const order = (label: string, fn: () => Promise<unknown> | undefined): HTMLButtonElement => {
      const b = document.createElement('button');
      b.textContent = label;
      b.style.cssText = 'cursor:pointer;background:#1d2635;color:#9cf;border:1px solid #2a3f5a;border-radius:5px;padding:3px 9px;font:600 12px system-ui';
      b.onclick = () => { sfx('sfx_droid_beep', { gain: 0.35, cooldownMs: 200 }); void fn()?.catch((e: Error) => this.note(e.message)); };
      return b;
    };
    const conn = () => atlas.conn;
    const id = d.droidId;
    btnRow.append(
      order('Run', () => conn()?.reducers.droidCommand({ droidId: id, cmd: 'run' })),
      order('Stop', () => conn()?.reducers.droidCommand({ droidId: id, cmd: 'stop' })),
      order('Scan', () => conn()?.reducers.droidCommand({ droidId: id, cmd: 'scan' })),
      order('Scan life', () => conn()?.reducers.droidCommand({ droidId: id, cmd: 'scanlife' })),
      order('Come', () => conn()?.reducers.droidCommand({ droidId: id, cmd: 'come' })),
      order('Follow', () => conn()?.reducers.droidCommand({ droidId: id, cmd: 'follow' })),
      order('Home', () => conn()?.reducers.droidCommand({ droidId: id, cmd: 'home' })),
      order('Recall', () => conn()?.reducers.recallDroid({ droidId: id })?.then(() => this.closeWin())),
    );

    const srcLabel = document.createElement('div');
    srcLabel.textContent = 'Droidscript program';
    srcLabel.style.cssText = 'font-weight:600;color:#aab;margin-bottom:3px';
    const src = document.createElement('textarea');
    src.value = d.programSrc || SAMPLE;
    src.spellcheck = false;
    src.style.cssText = 'width:100%;height:110px;background:#0c1220;color:#cfe;border:1px solid #24344c;'
      + 'border-radius:6px;font:12px/1.45 ui-monospace,monospace;padding:6px;resize:vertical;box-sizing:border-box';
    this.srcBox = src;
    const cheat = document.createElement('div');
    cheat.textContent = CHEAT_SHEET;
    cheat.style.cssText = 'font:10.5px/1.4 ui-monospace,monospace;color:#678;margin:4px 0 6px';
    const upload = order('▶ Upload & run program', () =>
      conn()?.reducers.droidProgram({ droidId: id, source: src.value })?.then(() => this.note('program accepted — running')));
    upload.style.width = '100%';

    const logLabel = document.createElement('div');
    logLabel.textContent = 'Log';
    logLabel.style.cssText = 'font-weight:600;color:#aab;margin:8px 0 3px';
    const log = document.createElement('div');
    log.style.cssText = 'height:120px;overflow-y:auto;background:#0c1220;border:1px solid #24344c;'
      + 'border-radius:6px;font:11px/1.5 ui-monospace,monospace;padding:6px;color:#9fb4cc;white-space:pre-wrap';
    this.logBox = log;

    const close = document.createElement('button');
    close.textContent = '✕';
    close.style.cssText = 'position:absolute;top:8px;right:10px;cursor:pointer;background:none;border:none;color:#89a;font-size:14px';
    close.onclick = () => this.closeWin();

    win.append(close, title, status, btnRow, srcLabel, src, cheat, upload, logLabel, log);
    document.body.appendChild(win);
    this.win = win;
    document.body.dataset.droidTerminal = '1'; // the Lua IDE may open while a droid is being programmed
    this.refreshWin();
    this.logTimer = window.setInterval(() => this.refreshWin(), 1000);
  }

  private refreshWin(): void {
    const conn = atlas.conn;
    if (!conn || this.winDroid === null) return;
    const d = conn.db.droidUnit.droidId.find(this.winDroid);
    if (!d) { this.closeWin(); return; }
    if (this.statusLine) {
      this.statusLine.textContent =
        `${d.kind} · mode ${d.mode} · at (${Math.round(d.posX)}, ${Math.round(d.posZ)}) · q${d.quality}`;
    }
    if (this.logBox) {
      let log: Array<{ t: string; msg: string }> = [];
      try { log = JSON.parse(d.logJson) as typeof log; } catch { /* fresh */ }
      const atBottom = this.logBox.scrollTop + this.logBox.clientHeight >= this.logBox.scrollHeight - 8;
      this.logBox.textContent = log.map((l) => {
        const t = new Date(Number(BigInt(l.t) / 1000n));
        return `[${t.getHours().toString().padStart(2, '0')}:${t.getMinutes().toString().padStart(2, '0')}] ${l.msg}`;
      }).join('\n');
      if (atBottom) this.logBox.scrollTop = this.logBox.scrollHeight;
    }
  }

  private note(text: string): void {
    bus.emit('atlas:status', { text });
  }

  private closeWin(): void {
    delete document.body.dataset.droidTerminal;
    if (this.logTimer !== null) { window.clearInterval(this.logTimer); this.logTimer = null; }
    this.win?.remove();
    this.win = null;
    this.winDroid = null;
    this.logBox = null;
    this.statusLine = null;
    this.srcBox = null;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    this.closeWin();
    this.clearUnits();
    this.hint?.remove();
  }
}
